import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../utils/cn';

const CustomSelect = ({ 
  options = [], 
  value = "", 
  onChange, 
  placeholder = "Select an option",
  className = "",
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const selectRef = useRef(null);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (option) => {
    setSelectedValue(option.value);
    onChange && onChange({ target: { value: option.value } });
    setIsOpen(false);
  };

  const selectedOption = options.find(option => option.value === selectedValue);

  return (
    <div ref={selectRef} className={cn("relative w-full", className)}>
      {/* Select Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={cn(
          "w-full h-[50px] rounded-md px-2 text-sm font-medium font-mono tracking-wide bg-transparent border border-dashed border-zinc-700 focus:border-zinc-400 focus:outline-none text-white flex items-center justify-between",
          disabled && "cursor-not-allowed opacity-50",
          !disabled && "hover:border-zinc-600 cursor-pointer"
        )}
      >
        <span className={cn(
          "truncate",
          !selectedOption && "text-zinc-500"
        )}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg
          className={cn(
            "w-4 h-4 transition-transform duration-200",
            isOpen && "rotate-180",
            !selectedOption && "text-zinc-500"
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown Options */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-md shadow-lg max-h-60 overflow-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-zinc-500 font-mono">
              No options available
            </div>
          ) : (
            options.map((option, index) => (
              <button
                key={option.value || index}
                type="button"
                onClick={() => handleSelect(option)}
                className={cn(
                  "w-full text-left px-3 py-2 text-sm font-mono hover:bg-zinc-800 transition-colors duration-150",
                  selectedValue === option.value && "bg-zinc-800 text-white",
                  selectedValue !== option.value && "text-zinc-300"
                )}
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
